import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { camelCase_to_Sentence, DeliveryType, getNiceDate, Incident, IncidentStatus, toTitleCase, } from '@iotv/datamodel';
import CheckIcon from '@mui/icons-material/Check';
import MailIcon from '@mui/icons-material/Mail';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SmsIcon from '@mui/icons-material/Sms';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { getExact } from '../../../data/daoFunctions/daoFunctions';
import { ComponentMode, Severity } from '../../../types/AppTypes';
import DetailCard from '../../../v2/components/generic/ObjectView/DetailCard/DetailCard';
import { getViewDefinitionFromUserAttributes } from '../../factories/AttributeDefinitions';
import { displayMessage } from '../../messages/displayMessage';
import { Link } from 'react-router-dom';
const debug = process.env.REACT_APP_DEBUG && false;
const getIncidentZombieFromSk = (sk) => {
    const keyparts = sk.split(':');
    const type = keyparts[0];
    const thingSk = keyparts.slice(1, 3).join(':');
    const id = sk;
    return { pk: thingSk, sk, type, id };
};
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const notificationRecordColumns = [
    {
        field: 'timestampMs',
        headerName: 'Time',
        ...columnLayout,
        minWidth: 150,
        valueFormatter: (value) => getNiceDate(value),
    },
    {
        field: 'deliveryType',
        headerName: 'Delivery',
        ...columnLayout,
        width: 70,
        renderCell: (params) => {
            let DeliveryIcon = undefined;
            switch (params.value) {
                case DeliveryType.EMAIL:
                    DeliveryIcon = _jsx(MailIcon, {});
                    break;
                case DeliveryType.SMS:
                    DeliveryIcon = _jsx(SmsIcon, {});
                    break;
                case DeliveryType.MOBILE_PUSH:
                    DeliveryIcon = _jsx(SmartphoneIcon, {});
                    break;
            }
            return _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: DeliveryIcon });
        },
    },
    {
        field: 'recipient',
        headerName: 'Recipient',
        ...columnLayout,
        valueGetter: (recipient) => recipient.name,
    },
    {
        field: 'escalationFrom',
        headerName: 'Escalated from',
        ...columnLayout,
    },
];
const notificationsColumns = [...notificationRecordColumns];
const historyRecordColumns = [
    {
        field: 'timestamp',
        headerName: 'Time',
        ...columnLayout,
        valueGetter: (value) => getNiceDate(value),
    },
    {
        field: 'event',
        headerName: 'Event',
        ...columnLayout,
        valueFormatter: (value) => toTitleCase(camelCase_to_Sentence(value)),
    },
    {
        field: 'initiator',
        headerName: 'Initiator',
        ...columnLayout,
        valueGetter: (value) => value.name,
    },
    {
        field: 'summary',
        headerName: 'Summary',
        ...columnLayout,
    },
    {
        field: 'triggeredVideoStreams',
        headerName: 'Triggered Video Streams',
        ...columnLayout,
        renderCell: ({ value, row }) => (_jsx(Typography, { sx: {}, children: value?.map((stream, i) => (_jsxs("span", { children: [_jsx(Link, { to: `/THINGVIEW/${stream.type}/${stream.id}?tabName=Media&selectedHLSPlaybackMode=ON_DEMAND&timestamp=${row.timestamp ?? ''}`, children: stream.name }), i < value.length - 1 ? ', ' : ''] }, stream.id || i))) })),
    },
];
const historyColumns = [...historyRecordColumns];
export const IncidentView = ({ contextCustomer, user, userFunctions, transactionFunctions, incidentSk, allowedActions, ...rest }) => {
    const [incident, setIncident] = useState(rest.incident ? new Incident(rest.incident) : undefined);
    const notificationRecords = useMemo(() => {
        if (incident && incident.notificationRecords)
            return Object.values(incident.notificationRecords)
                .reduce((acc, cur) => {
                acc = acc.concat(cur);
                return acc;
            }, [])
                .sort((a, b) => b.timestampMs - a.timestampMs)
                .map((record, i) => ({ ...record, id: i }));
        return [];
    }, [incident]);
    const historyRecords = useMemo(() => {
        if (incident && incident.history)
            return Object.values(incident.history).map((record, i) => ({ ...record, id: i }));
        return [];
    }, [incident]);
    const getIncident = async () => {
        const getRes = await getExact(getIncidentZombieFromSk(incidentSk));
        const zombie = getRes.data?.Items?.at(0);
        if (zombie)
            setIncident(new Incident(zombie));
        else
            displayMessage('Could not get item', 'MessageBar', userFunctions, Severity.warning);
    };
    useEffect(() => {
        if (incident === undefined)
            getIncident();
    }, [incidentSk]);
    const viewDefinition = incident && getViewDefinitionFromUserAttributes(incident.getUserAttributes());
    const tableProps = viewDefinition &&
        incident && {
        transactionFunctions,
        matchedPrimary: incident,
        actions: [],
        viewDefinition,
        functionOverrides: {
            mode: ComponentMode.VIEW,
            updateParentComponentItem: () => { },
            hasValidationErrors: () => { },
        },
    };
    const detailCardProps = viewDefinition &&
        incident && {
        mostProximateCustomer: undefined,
        viewDefinition,
        userFunctions,
        matchedPrimary: incident,
        transactionFunctions,
        allowedActions,
        // editMode={ComponentMode.EDIT}
    };
    if (!contextCustomer || !user || !detailCardProps || !tableProps)
        return _jsx(_Fragment, {});
    return (_jsxs(Stack, { gap: 1, children: [_jsx(DetailCard, { ...detailCardProps }), _jsxs(Button, { color: incident?.status === IncidentStatus.OPEN ? 'success' : 'warning', variant: "contained", disabled: !incident, onClick: () => {
                    if (!incident)
                        return;
                    switch (incident.status) {
                        case IncidentStatus.OPEN:
                            incident.closeIncident(user);
                            break;
                        case IncidentStatus.CLOSED:
                            incident.openIncident(user);
                            break;
                    }
                    transactionFunctions.saveObject(incident);
                }, startIcon: incident?.status === IncidentStatus.OPEN ? _jsx(CheckIcon, {}) : _jsx(WarningAmberIcon, {}), children: [incident?.status === IncidentStatus.OPEN && 'Close', incident?.status === IncidentStatus.CLOSED && 'Open'] }), _jsx(Typography, { variant: "h6", children: "Notifications" }), _jsx(DataGrid, { rows: notificationRecords, columns: notificationsColumns, disableRowSelectionOnClick: true, disableMultipleRowSelection: true, autosizeOnMount: true, autosizeOptions: { includeOutliers: true }, pageSizeOptions: [10], hideFooter: notificationRecords.length <= 10, hideFooterPagination: notificationRecords.length <= 10, initialState: { pagination: { paginationModel: { pageSize: 10 } } } }), _jsx(Typography, { variant: "h6", children: "Events" }), _jsx(DataGrid, { rows: historyRecords, columns: historyColumns, disableRowSelectionOnClick: true, disableMultipleRowSelection: true, autosizeOnMount: true, autosizeOptions: { includeOutliers: true }, pageSizeOptions: [10], hideFooter: historyRecords.length <= 10, hideFooterPagination: historyRecords.length <= 10, initialState: { pagination: { paginationModel: { pageSize: 10 } } } })] }));
};
